import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { Reveal } from "../components/Reveal";
import { IMG } from "../lib/assets";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const WIDGET_ID = "366b62703156323534333239";
const WIDGET_TOKEN = process.env.REACT_APP_MSG91_WIDGET_TOKEN;

function OtpLogin({ onSuccess, onCancel }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("phone");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentOk, setSentOk] = useState(false);
  const reqId = useRef(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const smsDown = !WIDGET_TOKEN;
  const inputCls = "w-full bg-transparent border-b border-vv-black/25 focus:border-vv-black outline-none py-3 text-sm placeholder:text-vv-stone transition-colors";

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  useEffect(() => {
    if (!WIDGET_TOKEN) return;
    if (!document.getElementById("msg91-otp-script")) {
      const script = document.createElement("script");
      script.id = "msg91-otp-script";
      script.src = "https://verify.msg91.com/otp-provider.js";
      script.async = true;
      script.onload = () => {
        try {
          window.initSendOTP({ widgetId: WIDGET_ID, tokenAuth: WIDGET_TOKEN, exposeMethods: true, isPlainCaptcha: false, success: () => {}, failure: () => setError("OTP service could not start. Refresh and try again.") });
        } catch { setError("OTP service failed to start. Refresh and try again."); }
      };
      script.onerror = () => setError("Unable to load the OTP service. Check your connection and retry.");
      document.head.appendChild(script);
    }
    let tries = 0;
    const poll = setInterval(() => {
      tries += 1;
      if (typeof window.sendOtp === "function" && typeof window.verifyOtp === "function") { setWidgetReady(true); clearInterval(poll); }
      else if (tries > 40) clearInterval(poll);
    }, 500);
    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSentOk(false);
    if (!widgetReady) { setBusy(false); setError("OTP service is still loading — try again in a moment."); return; }
    window.sendOtp(`91${phone}`,
      (data) => { reqId.current = data?.message || null; setStage("otp"); setSeconds(30); setSentOk(true); setBusy(false); },
      (err) => { setBusy(false); setError(err?.message || "OTP could not be sent. Check the number and try again."); });
  };

  const verify = (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    window.verifyOtp(otp, async (data) => {
      const accessToken = data?.access_token || data?.accessToken || data?.message;
      if (!accessToken) { setBusy(false); setError("Verification failed. Please try again."); return; }
      try {
        await api.post("/auth/otp/widget-verify", { access_token: accessToken, phone });
        const { data: me } = await api.get("/auth/me");
        toast.success("Signed in with mobile OTP");
        onSuccess(me);
      } catch (err) { setError(formatApiError(err)); setBusy(false); }
    }, () => { setBusy(false); setError("Incorrect or expired OTP. Please try again."); }, reqId.current);
  };

  const resend = () => {
    setBusy(true); setError("");
    const done = () => { setSeconds(30); setBusy(false); toast.success("OTP resent"); };
    if (typeof window.retryOtp === "function") window.retryOtp("text", done, () => { setBusy(false); setError("Could not resend OTP."); });
    else window.sendOtp(`91${phone}`, done, () => { setBusy(false); setError("Could not resend OTP."); });
  };

  return (
    <div>
      {stage === "phone" ? (
        <form onSubmit={send} className="space-y-6">
          <div className="flex">
            <span className="border-b border-vv-black/25 px-2 py-3 text-sm text-vv-muted">+91</span>
            <input data-testid="otp-login-phone" required inputMode="numeric" maxLength={10} value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="10-digit mobile number" className={`${inputCls} flex-1 min-w-0`} />
          </div>
          {error && <p data-testid="otp-login-error" className="text-xs text-vv-burgundy">{error}</p>}
          <button data-testid="otp-login-send" type="submit" disabled={busy || phone.length !== 10 || smsDown} className="vv-btn-dark w-full disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Send OTP
          </button>
          {smsDown && <p data-testid="otp-login-config-error" className="text-[11px] text-vv-muted">SMS sign-in is not configured yet. Use email or Google above.</p>}
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-6">
          <p className="text-sm text-vv-muted">OTP sent to <span className="text-vv-black font-medium">+91 {phone}</span>.</p>
          {sentOk && <p data-testid="otp-login-sent" className="text-xs text-vv-forest">SMS sent — check your messages.</p>}
          <input data-testid="otp-login-code" required inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit OTP" className={`${inputCls} tracking-[0.5em] text-center text-lg font-mono`} />
          {error && <p data-testid="otp-login-error" className="text-xs text-vv-burgundy">{error}</p>}
          <button data-testid="otp-login-verify" type="submit" disabled={busy || otp.length !== 6} className="vv-btn-dark w-full disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Verify & Sign In
          </button>
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
            <button type="button" data-testid="otp-login-change" onClick={() => { setStage("phone"); setOtp(""); setError(""); setSentOk(false); }} className="text-vv-muted hover:text-vv-black">Change Number</button>
            <button type="button" data-testid="otp-login-resend" onClick={resend} disabled={busy || seconds > 0} className="text-vv-muted hover:text-vv-black disabled:opacity-40">
              {seconds > 0 ? `Resend in ${seconds}s` : "Resend OTP"}
            </button>
          </div>
        </form>
      )}
      <button type="button" data-testid="otp-login-back" onClick={onCancel} className="mt-8 text-[11px] uppercase tracking-[0.25em] text-vv-muted hover:text-vv-black">← Back to email sign in</button>
    </div>
  );
}

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useShop();
  const navigate = useNavigate();
  const onAuthed = (data) => { setUser(data); navigate(data.role === "admin" ? "/admin" : "/account"); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      onAuthed(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-vv-black/25 focus:border-vv-black outline-none py-3 text-sm placeholder:text-vv-stone transition-colors";

  return (
    <main data-testid="auth-page" className="grid lg:grid-cols-2 min-h-[80vh]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMG.hero2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-vv-black/30" />
        <div className="absolute bottom-12 left-12 text-vv-ivory">
          <p className="font-display text-3xl">Timeless. Refined. Yours.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-20">
        <Reveal className="w-full max-w-sm">
          <p className="vv-overline text-vv-muted mb-4">{mode === "register" ? "Join the House" : "Welcome Back"}</p>
          <h1 className="font-display text-4xl tracking-tight mb-10">{mode === "register" ? "CREATE ACCOUNT" : mode === "otp" ? "SIGN IN WITH MOBILE" : "SIGN IN"}</h1>
          <button data-testid="google-login-button" type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/account";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 border border-vv-black/25 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-vv-black hover:text-vv-ivory transition-colors mb-8">
            <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.8z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1L1.2 17.2C3.2 21.2 7.3 24 12 24z"/><path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3L1.2 6.8C.4 8.4 0 10.1 0 12s.4 3.6 1.2 5.2L5 14.3z"/><path fill="#EA4335" d="M12 4.6c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.8 1.2 6.8L5 9.7c1-2.9 3.8-5.1 7-5.1z"/></svg>
            Continue with Google
          </button>
          <div className="flex border-b border-vv-black/10 mb-8">
            {[["login", "Sign In"], ["otp", "Mobile OTP"], ["register", "Register"]].map(([m, label]) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors ${mode === m ? "border-b border-vv-black -mb-px font-medium" : "text-vv-muted"}`}>
                {label}
              </button>
            ))}
          </div>
          {mode === "otp" ? (
            <OtpLogin onSuccess={onAuthed} onCancel={() => { setMode("login"); setError(""); }} />
          ) : (
          <form onSubmit={submit} className="space-y-6">
            {mode === "register" && (
              <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name" className={inputCls} />
            )}
            <input data-testid="auth-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email address" className={inputCls} />
            <input data-testid="auth-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password (min 6 characters)" className={inputCls} />
            {error && <p data-testid="auth-error" className="text-xs text-vv-burgundy">{error}</p>}
            <button data-testid="auth-submit-button" type="submit" disabled={busy} className="vv-btn-dark w-full disabled:opacity-50">
              {busy ? "One moment…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
          )}
          <p className="text-[11px] text-vv-muted mt-8 text-center">
            Prefer to shop as a guest? <Link to="/shop" className="vv-link-underline text-vv-black">Continue browsing</Link>
          </p>
        </Reveal>
      </div>
    </main>
  );
}
